import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NorthNorthamptonshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="North Northamptonshire Pothole Claims | RED Best Practice Rating | Fixtyer"
        description="North Northamptonshire earns RED for wider best practice from the DfT despite claiming innovation. Only 8.58% of B/C roads in good condition, 12.86% of U-roads in RED, and 68,678 pothole repairs in five years. See what the AMBER rating means for your claim."
        canonical="/councils/north-northamptonshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'North Northamptonshire', url: '/councils/north-northamptonshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'What does the RED best practice rating mean for my pothole claim?',
            answer: 'The DfT wider best practice scorecard measures whether a council adopts recognised efficiency and innovation practices — including technologies to fix and prevent potholes more efficiently. North Northamptonshire is RED despite listing Roadmender, thermal patching and spray injection in its own report. That gap between claimed innovation and official assessment is directly relevant to Section 58: you can ask whether the council\'s actual maintenance system — not its brochure — was reasonable for the defect that damaged your vehicle.',
          },
          {
            question: 'North Northamptonshire spends above its DfT allocation — can I still claim?',
            answer: 'Yes. The Spend scorecard is AMBER, not a bar to compensation. Section 58 turns on whether the specific defect was reasonably inspected and repaired. Capital spend exceeded DfT allocation in 2024/25 (£14.44m vs £9.47m) yet only 22% went on preventative carriageway work and 30% on reactive repairs including pothole filling. High aggregate spend does not prove your pothole was caught in time.',
          },
          {
            question: 'What if my pothole was on an unclassified road?',
            answer: 'U-roads make up 1,015km — 55% of the North Northamptonshire network. At the latest survey, 12.86% of U-roads were in RED condition (roughly 130km of residential and village routes). The council surveys annually using high-resolution camera vehicles, so ask when your road was last surveyed and what action followed a RED classification.',
          },
          {
            question: 'Only 8.58% of B/C roads are in good condition — how does that help?',
            answer: 'It shows systematic deterioration, not an isolated defect. Good-condition B/C roads have fallen from 17.17% in 2020 to 8.58% in 2024 while RED B/C roads rose from 6.94% to 10.42%. When over 91% of the B/C network is amber or red, the council cannot easily argue it maintains a stable, well-managed local road system under Section 58.',
          },
          {
            question: 'The council says preventative maintenance will increase from 2026 — does that weaken my claim?',
            answer: 'No — it is a forward-looking admission the current balance is insufficient. The council\'s own report states "The preventative approach to carriageway maintenance will drive a significant increase in preventative maintenance activities from 2026." For a defect before that shift, the published 15–22% preventative share and 29–32% reactive share are the relevant figures.',
          },
          {
            question: 'Does North Northamptonshire\'s annual camera survey block my Section 58 argument?',
            answer: 'Not necessarily. Annual PAS2161-aligned surveys show the council can know network condition — which raises the question of what it did with that knowledge. If your road was classified RED or amber at the last survey, prior reports exist, or reactive repairs hit 14,350 in 2024/25 alone, the issue is response time and repair quality on your specific defect, not whether surveys happen at all.',
          },
        ]}
      />
      <ServiceSchema
        councilName="North Northamptonshire Council"
        councilSlug="north-northamptonshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/north-northamptonshire"
        headline="North Northamptonshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">North Northamptonshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            North Northamptonshire: RED for Best Practice, AMBER on the Ground
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            North Northamptonshire's own transparency report lists Roadmender, thermal patching and spray injection
            as embedded innovations — yet the DfT rates its wider best practice <strong className="text-rose-400">RED</strong>.
            Overall is <strong className="text-amber-400">AMBER</strong> because only{' '}
            <strong className="text-rose-400">8.58% of B/C roads</strong> remain in good condition,{' '}
            <strong className="text-rose-400">12.86% of U-roads</strong> are RED, and the council filled an
            estimated <strong className="text-rose-400">68,678 potholes in five years</strong> while spending
            just 15–22% of its budget on preventative carriageway work.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">RED</div>
            <div className="text-white font-medium mt-2">Wider best practice scorecard</div>
            <div className="text-brand-300 text-sm mt-1">
              The DfT measures whether councils adopt recognised efficiency and innovation practices.
              North Northamptonshire fails that test — despite claiming a preventative asset management strategy
              in its own published report.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The RED Best Practice Gap */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The RED Best Practice Scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            What the DfT measures — and what North Northamptonshire's own report reveals
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We continue to explore innovations in materials and processes that will support cost-efficiency and sustainability. We have innovations working groups that explore ideas that can be trialled, and we also collaborate and share best practice with other local authorities who use the same service provider."
              source="North Northamptonshire Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Through our asset management approach, we use network intelligence to help prioritise future planned and preventative repairs. Modelling is used to help determine the level of funding that is needed and to help inform future investment needs. The preventative approach to carriageway maintenance will drive a significant increase in preventative maintenance activities from 2026."
              source="North Northamptonshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">What RED Means</h3>
              <p className="text-rose-800 text-sm mb-3">
                The DfT wider best practice scorecard assesses whether a council follows recognised highways
                maintenance efficiency practices — including technologies to fix and prevent potholes more
                efficiently. A RED rating means North Northamptonshire is among the lowest performers nationally
                on that measure.
              </p>
              <p className="text-rose-800 text-sm">
                That matters for Section 58: the council cannot point to official best-practice recognition
                while arguing its maintenance system was reasonable. The gap between claimed innovation and
                DfT assessment is yours to explore.
              </p>
            </div>

            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Numbers Behind RED</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Preventative carriageway spend: just 15–22% across 2022/23–2025/26</li>
                <li>• Reactive maintenance (including pothole filling): 29–32% every year</li>
                <li>• 2025/26 planned preventative works: 3.7km micro asphalt, 12.4km surface dressing, 1.2km resurfacing on a 1,853km network</li>
                <li>• Preventative increase promised — but only from 2026</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 58 and Section 41 Highways Act</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, North Northamptonshire must maintain its roads.
              Under Section 58, it can defend a claim only if it proves a reasonable inspection and repair
              system — and that the specific defect was not actionable under that system.
            </p>
            <p className="text-white font-medium">
              A RED best practice scorecard does not automatically win your claim — but it undercuts any
              blanket assertion that the council runs a modern, efficient maintenance regime. Pair it with
              the condition data, pothole repair volumes and your own evidence of prior reports.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of annual camera survey data from North Northamptonshire's own transparency report —
            A-roads losing green coverage, B/C roads collapsing, U-roads stuck in double-digit RED
          </p>

          {/* Network size */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">287</td>
                  <td className="px-6 py-4 text-center">15.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">551</td>
                  <td className="px-6 py-4 text-center">29.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">U roads</td>
                  <td className="px-6 py-4 text-center font-mono">1,015</td>
                  <td className="px-6 py-4 text-center">54.8%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">1,853</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (287km): green share halving</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.79%</td>
                    <td className="px-4 py-2 text-center">58.27%</td>
                    <td className="px-4 py-2 text-center">38.94%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4.1%</td>
                    <td className="px-4 py-2 text-center">62.8%</td>
                    <td className="px-4 py-2 text-center">33.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4.2%</td>
                    <td className="px-4 py-2 text-center">65.4%</td>
                    <td className="px-4 py-2 text-center">30.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.06%</td>
                    <td className="px-4 py-2 text-center">70.6%</td>
                    <td className="px-4 py-2 text-center">26.34%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">3.55%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">71.5%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">24.95%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Good-condition A-roads have fallen from 38.94% to 24.95% in four years. Over 71% are now amber —
              "maintenance may be required soon" in the council's own definitions.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (551km): only 8.58% in good condition</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6.94%</td>
                    <td className="px-4 py-2 text-center">75.89%</td>
                    <td className="px-4 py-2 text-center">17.17%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">10.3%</td>
                    <td className="px-4 py-2 text-center">76.5%</td>
                    <td className="px-4 py-2 text-center">13.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">11.4%</td>
                    <td className="px-4 py-2 text-center">77.2%</td>
                    <td className="px-4 py-2 text-center">11.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">9.87%</td>
                    <td className="px-4 py-2 text-center">81.54%</td>
                    <td className="px-4 py-2 text-center">8.59%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10.42%</td>
                    <td className="px-4 py-2 text-center font-bold">81%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8.58%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED B/C roads are up from 6.94% to 10.42%. Good-condition B/C roads have halved from 17.17%
              to 8.58%. Over 91% of the B/C network is now amber or red — maintenance needed or imminent.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <h3 className="font-semibold text-brand-900 px-6 pt-6 mb-4">U-roads (1,015km): RED condition holding above 12%</h3>
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10.77%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11.59%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.86%</td>
                </tr>
              </tbody>
            </table>
            <p className="text-surface-600 text-sm px-6 py-4">
              At 12.86% RED, roughly 130km of unclassified roads — estates, village streets and residential
              routes — are classified as needing maintenance. Unlike some councils, North Northamptonshire
              surveys U-roads annually.
            </p>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="Red - Should be considered for maintenance. Amber - Maintenance may be required soon. Green - No further investigation or treatment required."
              source="North Northamptonshire Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="However, it is recognised that limitations on funding may restrict maintenance spending in the short and medium term and in such situations the network will need to be proactively managed to maintain safety and availability."
              source="North Northamptonshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Spending Above Allocation, Still Mostly Reactive
          </h2>
          <p className="text-surface-600 mb-8">
            North Northamptonshire's AMBER spend scorecard — capital above DfT allocation, but preventative share stuck in the teens
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£8,404</td>
                  <td className="px-4 py-3 text-center">£8,175</td>
                  <td className="px-4 py-3 text-center">£7,180</td>
                  <td className="px-4 py-3 text-center">15%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£10,967</td>
                  <td className="px-4 py-3 text-center">£10,577</td>
                  <td className="px-4 py-3 text-center">£6,629</td>
                  <td className="px-4 py-3 text-center">16%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£9,473</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£14,440</td>
                  <td className="px-4 py-3 text-center">£7,041</td>
                  <td className="px-4 py-3 text-center">22%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£12,377</td>
                  <td className="px-4 py-3 text-center font-bold">£15,768</td>
                  <td className="px-4 py-3 text-center">£7,697</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">18%</td>
                  <td className="px-4 py-3 text-center">29%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Unitary Authority Context</h3>
              <p className="text-surface-600 text-sm">
                North Northamptonshire Council became a unitary authority in April 2021. Individual DfT funding
                allocations for North and West Northamptonshire were first made in April 2022 — so the spending
                table covers four financial years, not five. Pothole repair figures for 2020/21 and 2021/22 are
                estimated from pre-unitary data.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Preventative Gap</h3>
              <p className="text-amber-800 text-sm">
                Reactive maintenance — work needed to repair assets such as filling potholes — accounts for
                roughly double the preventative carriageway share every year. The council itself says a
                "significant increase" in preventative work will only come from 2026.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Reactive maintenance covers work that is needed to repair assets, such as filling potholes."
            source="North Northamptonshire Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            68,678 Pothole Repairs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated carriageway reactive repairs — the council's own count of defects filled
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">15,821</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">13,645</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">11,320</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">13,542</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14,350</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">68,678</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~38 Repairs a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, North Northamptonshire fills an estimated 38 carriageway defects
                per day. A network producing potholes at that rate is one where defects routinely form
                between inspections — exactly the scenario where prior reports and photographic evidence
                decide claims.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Potholes Are the Top Report</h3>
              <p className="text-rose-800 text-sm">
                The council states potholes are "the defect that is most reported through our online
                reporting tool" and that reactive spend "is based on an agreed risk based approach."
                If you reported your pothole online before the damage, that report is direct evidence
                of notice.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Potholes are the defect that is most reported through our online reporting tool. In addition to customer reporting, we also conduct regular inspections of the highway network, to identify any safety repairs that are needed. This drives the reactive spend, which is based on an agreed risk based approach."
            source="North Northamptonshire Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Survey methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Annual Surveys — And a Methodology Change Ahead
          </h2>
          <p className="text-surface-600 mb-8">
            North Northamptonshire surveys its network every year — but the standard is about to change again
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Since 2020, road condition assessments on the North Northamptonshire local road network have been captured annually, using high-resolution cameras mounted on vehicles that capture thousands of detailed images of the road surface as the vehicle drives along. These images are then assessed and analysed using specialist software. This is in line with the new national standard BSI PAS2161 as detailed below."
              source="North Northamptonshire Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="From 2026/27 a new methodology will be used based on the BSI PAS2161 standard. Local Highway Authorities will be required to use a supplier that has been accredited against PAS2161. This new standard will categorise roads into five categories instead of three to help government gain a more detailed understanding of road condition in England."
              source="North Northamptonshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Annual camera surveys mean the council cannot claim ignorance of network condition. For your claim, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last camera-surveyed, and what category was it assigned?</li>
              <li>• If classified RED or amber, what maintenance followed within the council's risk-based response times?</li>
              <li>• Did you report the defect online — the council's most-reported defect type?</li>
              <li>• How does a RED best practice scorecard sit alongside claimed PAS2161-aligned surveys?</li>
            </ul>
            <p className="text-white font-medium">
              Knowledge without timely action is the gap Section 58 exposes. Annual surveys strengthen your
              argument if the council knew — or should have known — your road was deteriorating.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER Council With a RED Best Practice Flag
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: North Northamptonshire is not West Northamptonshire — but the RED scorecard and condition data give you real leverage
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER spend — capital spend exceeded DfT allocation in 2024/25 (£14.44m vs £9.47m)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual camera surveys on all road classes including U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management policy and published innovation tools (Roadmender, thermal patching, spray injection)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Regular safety inspections alongside customer reporting
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing inspections and risk-based reactive repairs. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED wider best practice — lowest tier on DfT efficiency and innovation measures
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 8.58% of B/C roads in good condition; 91%+ amber or red
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12.86% of U-roads in RED — roughly 130km of residential routes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 68,678 estimated pothole repairs in five years — defects outpace prevention
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Just 15–22% preventative spend; council admits increase only from 2026
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Published admission that funding limitations may restrict maintenance spending
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with annual surveys and a documented inspection framework, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior online reports of the same pothole — proof of actual notice on the council's most-reported defect type</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Your road's survey classification — RED or amber at the last annual camera survey</li>
              <li>• The gap between claimed innovation (Roadmender, thermal patching) and the DfT's RED best practice rating</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites North Northamptonshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in North Northamptonshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED best-practice flag and 68,678 repairs in five years demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No RED best practice argument</li>
                <li>• No prior-report search</li>
                <li>• No survey classification request</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED best practice scorecard cited</li>
                <li>✅ B/C road decline documented (8.58% green)</li>
                <li>✅ 68,678 repairs in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to North Northamptonshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=north-northamptonshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My North Northamptonshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the RED best practice rating mean for my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT wider best practice scorecard measures whether a council adopts recognised efficiency
                and innovation practices — including technologies to fix and prevent potholes more efficiently.
                North Northamptonshire is RED despite listing Roadmender, thermal patching and spray injection
                in its own report. That gap between claimed innovation and official assessment is directly relevant
                to Section 58: you can ask whether the council's actual maintenance system — not its brochure —
                was reasonable for the defect that damaged your vehicle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                North Northamptonshire spends above its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The Spend scorecard is AMBER, not a bar to compensation. Section 58 turns on whether the
                specific defect was reasonably inspected and repaired. Capital spend exceeded DfT allocation in
                2024/25 (£14.44m vs £9.47m) yet only 22% went on preventative carriageway work and 30% on
                reactive repairs including pothole filling. High aggregate spend does not prove your pothole was caught in time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 1,015km — 55% of the North Northamptonshire network. At the latest survey,
                12.86% of U-roads were in RED condition (roughly 130km of residential and village routes).
                The council surveys annually using high-resolution camera vehicles, so ask when your road was
                last surveyed and what action followed a RED classification.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Only 8.58% of B/C roads are in good condition — how does that help?
              </h3>
              <p className="text-surface-600 text-sm">
                It shows systematic deterioration, not an isolated defect. Good-condition B/C roads have fallen
                from 17.17% in 2020 to 8.58% in 2024 while RED B/C roads rose from 6.94% to 10.42%. When over
                91% of the B/C network is amber or red, the council cannot easily argue it maintains a stable,
                well-managed local road system under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council says preventative maintenance will increase from 2026 — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it is a forward-looking admission the current balance is insufficient. The council's own
                report states "The preventative approach to carriageway maintenance will drive a significant
                increase in preventative maintenance activities from 2026." For a defect before that shift,
                the published 15–22% preventative share and 29–32% reactive share are the relevant figures.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does North Northamptonshire's annual camera survey block my Section 58 argument?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. Annual PAS2161-aligned surveys show the council can know network condition —
                which raises the question of what it did with that knowledge. If your road was classified RED
                or amber at the last survey, prior reports exist, or reactive repairs hit 14,350 in 2024/25 alone,
                the issue is response time and repair quality on your specific defect, not whether surveys happen at all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.northnorthants.gov.uk/highways-major-projects-plans-and-strategies/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              North Northamptonshire Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
